/**
 * Constants and configuration values for the FacilitiesWalkthrough module
 * 
 * This file centralizes all configuration values to make them easier to maintain
 * and modify without searching through multiple files.
 */

// Auto-save configuration
export const AUTO_SAVE = {
  DEBOUNCE_DELAY: 2000, // Wait 2 seconds after last change before auto-saving
  STATUS_DISPLAY_TIME: 3000, // Show save status message for 3 seconds
  MAX_RETRY_ATTEMPTS: 3, // Maximum times to retry a failed save
  RETRY_DELAY: 1000, // Delay between retry attempts in milliseconds
};

// File upload configuration
export const FILE_UPLOAD = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB per file
  MAX_FILES_PER_AREA: 10, // Maximum files per question area
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp'],
  MAX_FILENAME_LENGTH: 100,
};

// Form validation configuration
export const VALIDATION = {
  REQUIRED_FIELDS: ['strengths', 'opportunities'],
  MIN_TEXT_LENGTH: 10, // Minimum characters for text areas
  MAX_TEXT_LENGTH: 2000, // Maximum characters for text areas
};

// API endpoint constants
export const API_ENDPOINTS = {
  SURVEY_INFO: '/hd-api/facilities-walkthrough/info',
  SAVE_SURVEY: '/hd-api/facilities-walkthrough/save',
  SUBMIT_SURVEY: '/hd-api/facilities-walkthrough/submit',
  CHECK_COMPLETE: '/hd-api/facilities-walkthrough/complete/survey=',
  SAVED_ANSWERS: '/hd-api/facilities-walkthrough/user=',
  SAVED_IMAGES: '/hd-api/facilities-walkthrough/user=',
  ARCHIVE_SAVED: '/hd-api/facilities-walkthrough/user=',
  USER_INFO: '/hd-api/user/',
  USER_STORES: '/hd-api/user/stores/',
  GLOBAL_MESSAGES: '/hd-api/messages/global',
};

// Survey state constants
export const SURVEY_STATES = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SUBMITTED: 'submitted',
};

// Loading state constants
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Auto-save status constants
export const SAVE_STATUS = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
};

// File upload status constants
export const UPLOAD_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  ERROR: 'error',
};

// Responsibility options for issue assignment
export const RESPONSIBILITY_OPTIONS = [
  { value: '', label: 'Please select' },
  { value: 'Facilities', label: 'Facilities' },
  { value: 'Store', label: 'Store' },
];

// Default form values
export const DEFAULT_FORM_VALUES = {
  strengths: '',
  opportunities: '',
};

// UI configuration
export const UI_CONFIG = {
  TABS_VARIANT: 'scrollable',
  TABS_SCROLL_BUTTONS: 'auto',
  MAX_CONTAINER_WIDTH: 'md',
  PAPER_ELEVATION: 1,
  DIVIDER_MARGIN: '12px 8px',
};

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  AUTHENTICATION_ERROR: 'Authentication failed. Please log in again.',
  VALIDATION_ERROR: 'Please fix the validation errors before continuing.',
  SAVE_ERROR: 'Failed to save survey. Please try again.',
  SUBMIT_ERROR: 'Failed to submit survey. Please try again.',
  LOAD_ERROR: 'Failed to load survey data. Please refresh and try again.',
  FILE_TOO_LARGE: 'File size exceeds the maximum allowed size.',
  INVALID_FILE_TYPE: 'File type not allowed. Please use image files only.',
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
};

// Success messages
export const SUCCESS_MESSAGES = {
  SURVEY_SAVED: 'Survey saved successfully',
  SURVEY_SUBMITTED: 'Survey submitted successfully',
  FILES_UPLOADED: 'Files uploaded successfully',
};

// Question response values
export const QUESTION_RESPONSES = {
  PASS: '1',
  FAIL: '0',
};

// Tab navigation constants
export const TAB_NAVIGATION = {
  SUMMARY_TAB_LABEL: 'Summary',
  BACK_BUTTON_LABEL: 'Back',
  NEXT_BUTTON_LABEL: 'Next',
  SUBMIT_BUTTON_LABEL: 'Submit',
};

// Local storage keys (if needed for future enhancements)
export const STORAGE_KEYS = {
  DRAFT_SURVEY: 'facilities_walkthrough_draft',
  USER_PREFERENCES: 'facilities_walkthrough_preferences',
};

/**
 * Helper function to get file size limit in human-readable format
 * @returns {string} Formatted file size limit
 */
export const getFileSizeLimit = () => {
  const sizeInMB = FILE_UPLOAD.MAX_FILE_SIZE / (1024 * 1024);
  return `${sizeInMB}MB`;
};

/**
 * Helper function to get allowed file types as a readable string
 * @returns {string} Comma-separated list of allowed file types
 */
export const getAllowedFileTypes = () => {
  return FILE_UPLOAD.ALLOWED_TYPES
    .map(type => type.split('/')[1].toUpperCase())
    .join(', ');
};

/**
 * Helper function to check if a value represents a failing response
 * @param {string|number} value - The response value to check
 * @returns {boolean} True if the value represents a failure
 */
export const isFailingResponse = (value) => {
  return String(value) === QUESTION_RESPONSES.FAIL;
};

/**
 * Helper function to check if a value represents a passing response
 * @param {string|number} value - The response value to check
 * @returns {boolean} True if the value represents a pass
 */
export const isPassingResponse = (value) => {
  return String(value) === QUESTION_RESPONSES.PASS;
};